import React, { useState } from 'react';
import { authenticateUser, createAuthToken, setAuthCookie, redirectToApp } from '../utils/auth';

interface LoginFormProps {
  onLoginSuccess: () => void;
}

const allowedReturnOrigins = [
  'http://localhost:5173',
  'https://exact-plytix-sync.vercel.app',
  'https://exact-plytix-sync-staging.vercel.app',
];

function isAllowedReturn(urlStr: string | null) {
  if (!urlStr) return false;
  try {
    const u = new URL(urlStr);
    if (allowedReturnOrigins.includes(u.origin)) return true;
    if (u.hostname.startsWith('exact-plytix-sync-staging-') && u.hostname.endsWith('.vercel.app')) return true;
    return false;
  } catch {
    return false;
  }
}

function getReturnUrl(defaultUrl: string) {
  const params = new URLSearchParams(window.location.search);
  const returnUrl = params.get('return');
  if (isAllowedReturn(returnUrl)) return returnUrl!;
  return defaultUrl;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const client = authenticateUser(username, password);
      if (client) {
        const token = await createAuthToken(client);
        setAuthCookie(token);

        const target = getReturnUrl(client.url);
        setTimeout(() => {
          redirectToApp(target, token);
        }, 1000);

        onLoginSuccess();
      } else {
        setError('Invalid username or password');
      }
    } catch (err) {
      setError('An error occurred while signing in');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto bg-white rounded-lg shadow-lg border border-gray-100">
      <div className="p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
              Username
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              className="w-full px-3 py-2 bg-gray-100 border-0 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
              placeholder="Enter your username"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-3 py-2 bg-gray-100 border-0 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
              placeholder="Enter your password"
            />
          </div>

          {error && <div className="text-red-600 text-sm text-center">{error}</div>}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-600 hover:to-purple-700 text-white font-medium py-2 px-4 rounded-md transition-all duration-200 disabled:opacity-50"
          >
            {isLoading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
};
